import { Injectable } from '@angular/core';
import {
	Assessment,
	AssessmentService,
	Group,
	GroupService,
	Organization,
	OrganizationService,
} from '@services/member';
import { Domain, DomainService, TimespanService } from '@services/public';
import { Select, SelectKey } from '@util';
import { uuid } from 'jakapa-utilities-lite';
import { ALL_USERS, LeaderUserService, User } from '../user.service';
import { KeySubscription, LeaderReportFilters } from './common';

@Injectable({
	providedIn: 'root',
})
export class LeaderReportFilterService {
	constructor(
		private _assessmentSvc: AssessmentService,
		private _domainSvc: DomainService,
		private _groupSvc: GroupService,
		private _organizationSvc: OrganizationService,
		private _publicTimespanSvc: TimespanService,
		private _userSvc: LeaderUserService,
	) {}

	getFilters(
		filterKeys: Array<keyof LeaderReportFilters>,
	): LeaderReportFilters {
		const filters = <LeaderReportFilters>filterKeys.reduce<
			Partial<LeaderReportFilters>
		>((previousValue, currentValue) => {
			// View all is not mirred and must always be initialized independently.
			if (currentValue === 'viewAll') {
				previousValue[currentValue] = new Select<any>(SelectKey.VIEW_ALL);
				previousValue[currentValue].options = [null, true, false];
				previousValue[currentValue].loaded = true;
			} else previousValue[currentValue] = new Select<never>();
			return previousValue;
		}, {});
		return filters;
	}

	async initializeFilters(
		key: symbol,
		filters: LeaderReportFilters,
		subscriptions: Array<KeySubscription>,
	): Promise<void> {
		if ('assessment' in filters) {
			this._assessmentSvc
				.getAssessments()
				.subscribe((res: Array<Assessment>) => {
					res = res.filter((assessment) => assessment.groupUUID);
					res.unshift({ name: '', uuid: null });
					subscriptions.push({
						key,
						subscription: filters.group.onChange.subscribe((group: Group) => {
							if (!group || group.uuid === null) {
								filters.assessment.options = res.filter(
									(assessment: Assessment) =>
										assessment.uuid === null ||
										filters.group.options.findIndex(
											(group: Group) => group.uuid === assessment.groupUUID,
										) !== -1,
								);
							} else
								filters.assessment.options = res.filter(
									(assessment: Assessment) =>
										assessment.uuid === null ||
										group.uuid === assessment.groupUUID,
								);
						}),
					});
					filters.assessment.loaded = true;
				});
		}
		if ('user' in filters) {
			this._userSvc.getUsers().subscribe((res: Array<User>) => {
				// Remove duplicate user entries.
				const allUsers: Array<User> = [
					ALL_USERS,
					...[...new Set(res.map((user: User) => user.uuid))].map(
						(uuid: uuid) => res.find((user: User) => user.uuid === uuid),
					),
				];
				subscriptions.push({
					key,
					subscription: filters.group.onChange.subscribe((group: Group) => {
						if (!group || group.uuid === null) {
							filters.user.options = allUsers.filter(
								(user: User) =>
									user.uuid === null ||
									filters.group.options.some(
										(group: Group) =>
											!!group.memberUUIDs &&
											group.memberUUIDs.includes(user.uuid),
									),
							);
						} else
							filters.user.options = allUsers.filter(
								(user: User) =>
									user.uuid === null ||
									(!!group.memberUUIDs &&
										group.memberUUIDs.includes(user.uuid)),
							);
					}),
				});
				filters.user.loaded = true;
			});
		}
		if ('group' in filters) {
			this._groupSvc
				.getGroups(true, (group: Group) => group.role !== null)
				.then(async (value: Array<Group>) => {
					subscriptions.push({
						key,
						subscription: filters.organization.onChange.subscribe(
							(organization: Organization) => {
								if (!organization || organization.uuid === null) {
									filters.group.options = value;
								} else
									filters.group.options = value.filter(
										(group: Group) =>
											group.uuid === null ||
											group.organizationUUID === organization.uuid,
									);
							},
						),
					});
					const promises: Array<Promise<boolean>> = [];
					if ('assessment' in filters) promises.push(filters.assessment.loaded);
					if ('user' in filters) promises.push(filters.user.loaded);
					Promise.all(promises).then(() => (filters.group.loaded = true));
				});
		}
		this._organizationSvc
			.getOrganizations((organization: Organization) => organization.leader)
			.then(async (value: Array<Organization>) => {
				if ('group' in filters) await filters.group.loaded;
				filters.organization.options = value;
				filters.organization.loaded = true;
			});
		if ('skill' in filters) {
			subscriptions.push({
				key,
				subscription: filters.domain.onChange.subscribe((domain: Domain) => {
					filters.skill.options = Object.values(domain.skills);
				}),
			});
			filters.skill.loaded = true;
		}
		filters.domain.options = this._domainSvc.getDomains();
		filters.domain.loaded = true;
		if ('timespan' in filters) {
			filters.timespan.options = this._publicTimespanSvc.timespans;
			filters.timespan.loaded = true;
		}
		await Promise.all(Object.values(filters).map((filter) => filter.loaded));
	}
}
